import { merge } from 'lodash';
import {
  BranchOfficeCreateDataProvider,
  BranchOfficeDetailDataProvider,
  BranchOfficeEditDataProvider,
  BranchOffices,
} from 'ogp/components/views/management/branch-offices';
import { WorkTemplateCreateDataProvider } from 'ogp/components/views/planning/work-templates/pages/create/work-template-create';
import { JobsDataProvider } from 'ogp/components/views/planning/work-templates/work-templates';
import {
  UserCreate,
  UserDetailDataProvider,
  UserEditDataProvider,
} from 'ogp/components/views/management/users';
import { Users } from 'ogp/components/views/management/users/users';
import { useGetOGPUserInfo } from 'ogp/hooks/queryHooks/users/use-get-ogp-user-info';
import { useCompanyRelativePath } from 'ogp/hooks';
import { BaseLayout } from 'ogp/layouts/base-layout';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { NotFound } from 'shared/components/views/404/404';
import { Unauthorized } from 'shared/components/views/unauthorized/Unauthorized';
import { useAlert } from 'shared/components/alerts';
import { ExtendedError, ExtendedErrorType } from 'shared/services/abstractApiService';
import { RenderUnsettledUI } from 'shared/components';
import { CompanyDetail, CompanyEdit } from './components/views/management/company';
import { SharedCheckoutsArchive, SharedCheckoutsInbox } from './components/views/administration';
import {
  FlexPoolCreateDataProvider,
  FlexPoolEditDataProvider,
  FlexPools,
} from './components/views/planning/flex-pools';
import { RouteKeys, routes } from './Routes';
import { Planning } from './components/views/planning/planning/planning';
import { DialogProvider } from './providers/dialog-provider/dialog-provider';
import { ProjectsPlanning } from './components/views/planning/projects-planning/projects-planning';
import { ProjectCreateDataProvider } from './components/views/planning/projects-planning/components/forms/project-create-data-provider';
import { ProjectUpdateDataProvider } from './components/views/planning/projects-planning/components/forms/project-update-data-provider';
import { Departments } from './components/views/management/departments';
import { DepartmentDetailDataProvider } from './components/views/management/departments/pages/department-detail';
import { DepartmentCreateDataProvider } from './components/views/management/departments/pages/create/department-create';
import { DepartmentEditDataProvider } from './components/views/management/departments/pages/edit/department-edit';
import {
  BillingEntityCreate,
  BillingEntityDetail,
  BillingEntityEditProvider,
} from './components/views/management/billing-entities';
import { Legal } from './components/views/management/legal';
import { ProjectDuplicateDataProvider } from './components/views/planning/projects-planning/components/forms/project-duplicate-data-provider';
import { BranchOfficeDuplicateDataProvider } from './components/views/management/branch-offices/pages/create/branch-office-duplicate';
import { DepartmentDuplicateDataProvider } from './components/views/management/departments/pages/create/department-duplicate';
import { BillingEntityDuplicateDataProvider } from './components/views/management/billing-entities/pages/create/billing-entity-duplicate';
import { WorkTemplateDuplicateDataProvider } from './components/views/planning/work-templates/pages/create/work-template-duplicate';
import { Dashboard } from './components/views/planning/dashboard/dashboard';
import { WorkTemplateEditDataProvider } from './components/views/planning/work-templates/pages/edit/work-template-edit';
import { WorkTemplateDetailDataProvider } from './components/views/planning/work-templates/pages/detail/work-template-detail';
import { Workforce } from './components/views/management/workforce/workforce';
import { EmployeeDetail } from './components/views/management/workforce/components/employee-detail/employee-detail';
import { EmployeeCreate } from './components/views/management/workforce/components/employee-create/employee-create';
import { EmployeeUpdate } from './components/views/management/workforce/components/employee-edit/employee-update';
import { AvailabilityPageDataProvider } from './components/views/availability/pages/availability-page';
import { SettingsPageDataProvider } from './components/views/settings/settings/settings-detail-page';
import { SettingsEditPageDataProvider } from './components/views/settings/settings/settings-edit/settings-edit-page';
import { RouteRenderer } from '../router';
import { hqPrefix, RouteDefinition } from '../Routes';

// elements defined separately per route in order to prevent circular dependencies
const components: Record<RouteKeys, Partial<RouteDefinition>> = {
  Unauthorized: { element: <Unauthorized /> },
  Dashboard: { element: <Dashboard /> },
  BillingEntitiesDetail: { element: <BillingEntityDetail /> },
  BillingEntitiesEdit: { element: <BillingEntityEditProvider /> },
  BillingEntitiesCreate: { element: <BillingEntityCreate /> },
  BillingEntitiesDuplicate: { element: <BillingEntityDuplicateDataProvider /> },
  WorkTemplates: { element: <JobsDataProvider /> },
  WorkTemplatesEdit: { element: <WorkTemplateEditDataProvider /> },
  WorkTemplatesCreate: { element: <WorkTemplateCreateDataProvider /> },
  WorkTemplatesDuplicate: { element: <WorkTemplateDuplicateDataProvider /> },
  WorkTemplatesDetail: { element: <WorkTemplateDetailDataProvider /> },
  ShiftPlanning: { element: <Planning /> },
  FlexPools: { element: <FlexPools /> },
  Company: { element: <CompanyDetail /> },
  CompanyEdit: { element: <CompanyEdit /> },
  BranchOffices: { element: <BranchOffices /> },
  BranchOfficesEdit: { element: <BranchOfficeEditDataProvider /> },
  BranchOfficesCreate: { element: <BranchOfficeCreateDataProvider /> },
  BranchOfficesDuplicate: { element: <BranchOfficeDuplicateDataProvider /> },
  BranchOfficesDetail: { element: <BranchOfficeDetailDataProvider /> },
  Users: { element: <Users /> },
  UsersEdit: { element: <UserEditDataProvider /> },
  UsersCreate: { element: <UserCreate /> },
  UsersDetail: { element: <UserDetailDataProvider /> },
  CheckoutsInbox: { element: <SharedCheckoutsInbox /> },
  CheckoutsArchive: { element: <SharedCheckoutsArchive /> },
  FlexPoolCreate: { element: <FlexPoolCreateDataProvider /> },
  FlexPoolEdit: { element: <FlexPoolEditDataProvider /> },
  Projects: { element: <ProjectsPlanning /> },
  ProjectsCreate: { element: <ProjectCreateDataProvider /> },
  ProjectsDuplicate: { element: <ProjectDuplicateDataProvider /> },
  ProjectsEdit: { element: <ProjectUpdateDataProvider /> },
  Departments: { element: <Departments /> },
  DepartmentDetail: { element: <DepartmentDetailDataProvider /> },
  DepartmentCreate: { element: <DepartmentCreateDataProvider /> },
  DepartmentDuplicate: { element: <DepartmentDuplicateDataProvider /> },
  DepartmentEdit: { element: <DepartmentEditDataProvider /> },
  Legal: { element: <Legal /> },
  Workforce: { element: <Workforce /> },
  EmployeeDetail: { element: <EmployeeDetail /> },
  EmployeeCreate: { element: <EmployeeCreate /> },
  EmployeeUpdate: { element: <EmployeeUpdate /> },
  Availability: {
    element: <AvailabilityPageDataProvider />,
  },
  Settings: {
    element: <SettingsPageDataProvider />,
  },
  SettingsEdit: {
    element: <SettingsEditPageDataProvider />,
  },
};

// TODO stop merging this as a side effect
const items = merge(routes, components) as Record<string, RouteDefinition>;

const companyBasePath = `:companyHash`;

const Router = () => {
  const { alertError } = useAlert();
  const generatePath = useCompanyRelativePath();
  const userQuery = useGetOGPUserInfo();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      userQuery.error instanceof ExtendedError &&
      userQuery.error.type === ExtendedErrorType.ImposterMissingCompanyId
    ) {
      alertError(userQuery.error);
      navigate(`${hqPrefix}/sales/companies`);
    }
  }, [alertError, userQuery.error, navigate]);

  if (userQuery.status !== 'success') {
    return <RenderUnsettledUI data={userQuery} />;
  }

  return (
    <DialogProvider>
      <Routes>
        <Route path={`${companyBasePath}/planning`} element={<Navigate to="shifts" />} />
        {userQuery.data
          ? RouteRenderer(
              Object.values(items),
              companyBasePath,
              userQuery.data.userInfo,
              BaseLayout
            )
          : null}
        <Route
          path={`${companyBasePath}/checkout`}
          element={<Navigate replace to={generatePath(items.CheckoutsInbox)} />}
        />
        <Route path={'/'} element={<Navigate replace to={generatePath(items.Dashboard)} />} />
        <Route path="/*" element={<NotFound />} />
      </Routes>
    </DialogProvider>
  );
};

export { Router };
